import { Table } from 'shared/ui/Table/Table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button } from '@mui/material'
import { exportUrls } from './utility'
import {
  useCantIncludeColumns,
  useSelectColumn,
  useWithoutAltColumns,
} from './columns'
import { useListSwap } from 'shared/hooks'
import { SelectedUrl } from './SelectedUrl'
import { sortedTreeUrlsAtom, urlSortAtom } from './atoms'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { InfoDisplay } from '../../utility/Containers'

// UrlCsvExport - shows all images that do not currently have alt text
// From this component you can export all urls for those images.
// You can include/exclude any subset of those images for export
export function UrlCsvExport() {
  const { withoutAltText } = useAtomValue(sortedTreeUrlsAtom)
  const [canExport, setCanExport] = useState<string[]>([])
  const [cantExport, setCantExport] = useState<string[]>([])
  const [selectedUrl, setSelectedUrl] = useState<string>('')

  useEffect(() => {
    const { can, cant } = withoutAltText.reduce<{
      can: string[]
      cant: string[]
    }>(
      ({ can, cant }, url) => {
        if (url.startsWith('data')) {
          cant.push(url)
        } else {
          can.push(url)
        }
        return { can, cant }
      },
      { can: [], cant: [] },
    )
    setCanExport(can)
    setCantExport(cant)
  }, [withoutAltText])

  const urlSort = useAtomValue(urlSortAtom)

  const { toInclude, toExclude, onInclude, onExclude, includeAll, excludeAll } =
    useListSwap({
      items: canExport,
      sort: urlSort,
    })
  const onLoadError = useCallback((url: string) => {
    setCanExport((prev) => prev.filter((u) => u !== url))
    setCantExport((prev) => [...prev, url])
  }, [])

  const incCols = useWithoutAltColumns(onExclude, 'Exclude', onLoadError)
  const excCols = useWithoutAltColumns(onInclude, 'Include', onLoadError)
  const cantCols = useCantIncludeColumns()
  const selectCol = useSelectColumn('Select', setSelectedUrl, 'SEL')
  const { includeColumns, excludeColumns, cantColumns } = useMemo(() => {
    return {
      includeColumns: [...incCols, selectCol],
      excludeColumns: [...excCols, selectCol],
      cantColumns: [...cantCols, selectCol],
    }
  }, [incCols, excCols, selectCol, cantCols])

  function exportWithoutAltText() {
    exportUrls(...toInclude)
  }

  return (
    <Box>
      <Info />
      <Box>
        <Button
          onClick={exportWithoutAltText}
        >{`Export ${toInclude.length} Urls`}</Button>
      </Box>
      <Box padding="10px">
        <Button onClick={excludeAll}>Exclude All</Button>
        <Table<string> columns={includeColumns} items={toInclude} />
      </Box>
      <Box padding="10px">
        <Button onClick={includeAll}>Include All</Button>
        <Table<string> columns={excludeColumns} items={toExclude} />
      </Box>
      <Box padding="10px">
        <Box>Cant Export {cantExport.length}</Box>
        <Table<string> columns={cantColumns} items={cantExport} />
      </Box>
      <Box padding="10px">
        <SelectedUrl url={selectedUrl} onDeselect={() => setSelectedUrl('')} />
      </Box>
    </Box>
  )
}

function Info() {
  return (
    <InfoDisplay>
      <Box>
        This component presents to the user all images that do not have Alt
        Text.
      </Box>
      <Box>
        As the user scrolls through the table we try to retrieve the image, if
        the image can not be retrieved it is added to the Cant Export table
        below.
      </Box>
      <Box>
        The user can export these urls to download a list of all urls missing
        alt text.
      </Box>
      <Box>
        Selecting an image will give you more details about the image and where
        it is in the tree at the bottom of this page.
      </Box>
    </InfoDisplay>
  )
}
